import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { LookupIcon } from "@/assets/icons";
import DropdownMultiSelect, {
  IMultiOptionItem
} from "@/components/ui/dropdownMultiSelect";
import { useAnalysisSummaryDataContext } from "@/contexts/AnalysisSummaryDataContext";
import { useMap } from "@/hooks/map/core/useMapContext";
import { RootState } from "@/store";
import { setGridsSelected } from "@/store/slice/analysisSummary";
import { DatasetData } from "@/types/dataset";

import { twMerge } from "tailwind-merge";

import DatasetSection from "./datasetSection";

export const MAX_GRIDS_SELECTED = 3; // Maximum number of grids that can be selected
export const GRID_ID_ATTRIBUTE = "Grid ID"; // Attribute name for Grid ID in the dataset
export const CATCHMENT_ID_ATTRIBUTE = "Catchment ID"; // Attribute name for Catchment ID in the dataset
export const POLYGON_ID_ATTRIBUTE = "Polygon ID"; // Attribute name for Polygon ID in the dataset

export type AnalysisAreaData = {
  id: string;
  dataset_name: string;
  rows: { values: { value: string }[] }[];
  columns: { name: string }[];
  score: string;
};

export interface GridOptionItem extends IMultiOptionItem {
  score: number;
}
interface FilterByAnalysisAreaProps {
  dynamicChartRef: React.RefObject<(HTMLDivElement | null)[]>;
  isExpanded?: boolean;
}

const getContainerClassName = (
  isExpanded: boolean,
  isAllSelected: boolean,
  gridsSelected: string[]
) => {
  if (isExpanded) {
    if (isAllSelected) {
      return "flex flex-row 2xl:w-[28%]";
    }
    if (gridsSelected.length === 1) {
      return "flex flex-col md:flex-row 2xl:w-[28%]";
    }
    return "min-[1366px]:flex min-[1366px]:flex-row gap-3 2xl:w-[34%]";
  }

  let className;
  if (isAllSelected) {
    className = "flex flex-row";
  } else if (gridsSelected.length === 1) {
    className = "flex";
  } else {
    className = "flex flex-col";
  }

  return className;
};

const getDropdownClassName = (
  isExpanded: boolean,
  isAllSelected: boolean,
  gridsSelected: string[]
) => {
  if (isExpanded) {
    return isAllSelected || gridsSelected.length === 1 ? "w-full" : "";
  }
  return "w-full";
};

const getScoreContainerClassName = (
  isExpanded: boolean,
  isAllSelected: boolean,
  gridsSelected: string[]
) => {
  if (isExpanded) {
    if (isAllSelected) {
      return "items-center justify-between flex-row overflow-auto";
    }
    if (gridsSelected.length === 1) {
      return "items-center justify-between flex-row overflow-auto mt-0 md:mt-2";
    }
    return "min-[1366px]:flex-row 2xl:w-3/4 gap-3 justify-between min-[1366px]:items-center";
  }

  if (isAllSelected || gridsSelected.length === 1) {
    return "items-center justify-between flex-row";
  }

  return "flex flex-col mt-0";
};

const getGridContainerClassName = (
  isExpanded: boolean,
  gridsSelected: string[]
) => {
  if (isExpanded) {
    return "min-[1366px]:flex-row justify-between transition-all min-[1366px]:justify-end min-[1366px]:w-full gap-2 xl:space-x-8 xl:w-3/5 overflow-x-auto";
  }
  if (gridsSelected.length === 1) {
    return "";
  }
  return "mt-2 ";
};

const FilterByAnalysisArea = ({
  dynamicChartRef,
  isExpanded = false
}: FilterByAnalysisAreaProps) => {
  const dispatch = useDispatch();
  const { gridsSelected, spatialAnalysisDatasetId } = useSelector(
    (state: RootState) => state.analysisSummary
  );

  const { map, handleHighlightGridSelection } = useMap();

  const { gridValue, isAllSelected, originalDatasets, getTypeSummary } =
    useAnalysisSummaryDataContext();

  const containerClassName = getContainerClassName(
    isExpanded,
    isAllSelected,
    gridsSelected
  );

  const dropdownClassName = getDropdownClassName(
    isExpanded,
    isAllSelected,
    gridsSelected
  );

  const hasHighlightedRef = useRef<boolean>(false);

  const handleSelectGrid = (values: string[]) => {
    let resolvedGrids: string[] = [];

    if (values.length > 0) {
      resolvedGrids = values;
    } else if (gridValue?.length > 0) {
      resolvedGrids = [gridValue[0].value];
    }

    dispatch(setGridsSelected(resolvedGrids));

    if (map && spatialAnalysisDatasetId) {
      handleHighlightGridSelection(spatialAnalysisDatasetId, resolvedGrids);
    }
  };

  useEffect(() => {
    if (!map || !spatialAnalysisDatasetId) return;
    if (hasHighlightedRef.current) {
      // After mount: user click from map to gridsSelected changes
      handleHighlightGridSelection(spatialAnalysisDatasetId, gridsSelected);
    } else {
      // First mount: highlight + fallback idle
      hasHighlightedRef.current = true;
      handleHighlightGridSelection(spatialAnalysisDatasetId, gridsSelected);
      map.once("idle", () => {
        handleHighlightGridSelection(spatialAnalysisDatasetId, gridsSelected);
      });
    }
  }, [
    map,
    spatialAnalysisDatasetId,
    gridsSelected,
    handleHighlightGridSelection
  ]);

  return (
    <>
      <div className="flex flex-col gap-2">
        <span className="text-sm font-semibold text-txtHeading">
          Filter by Analysis Area
        </span>
        <div
          aria-label="filter-by-analysis-area"
          className={twMerge("gap-3 items-center", containerClassName)}
        >
          <DropdownMultiSelect
            className={twMerge("mt-3 h-14", dropdownClassName)}
            buttonClassName="py-4 h-[52px]"
            name="gridID/catchmentID/polygonID"
            placeholder="GridID/CatchmentID/PolygonID"
            data={gridValue}
            labelBorder={""}
            onSelect={handleSelectGrid}
            defaultValue={gridsSelected}
            showSelectAll
            maxSelected={MAX_GRIDS_SELECTED}
            selectedCountPlaceHolder={`${getTypeSummary().toLowerCase()} selected`}
          />
          <div
            className={twMerge(
              "px-3 py-2 w-full flex bg-teal-50 border border-teal-500 rounded-xl flex-col mt-2",
              getScoreContainerClassName(
                isExpanded,
                isAllSelected,
                gridsSelected
              )
            )}
          >
            <div className="flex gap-2 w-[80px]">
              <LookupIcon className="w-5 h-5 fill-teal-500" />
              <span className="text-sm font-semibold">Score</span>
            </div>
            {isAllSelected ? (
              <div className="flex flex-col">
                <span className="text-[10px] font-semibold text-teal-500">
                  {getTypeSummary()}ID: All
                </span>
                <span className="text-sm font-semibold text-teal-500">N/A</span>
              </div>
            ) : (
              <div
                className={twMerge(
                  "flex justify-between",
                  getGridContainerClassName(isExpanded, gridsSelected)
                )}
              >
                {gridsSelected.map((grid) => {
                  const gridData = gridValue.find(
                    (item) => item.value === grid
                  );
                  if (!gridData) return null;
                  return (
                    <div
                      key={gridData.value}
                      className={twMerge(
                        "flex flex-col",
                        gridsSelected.length === 2 && "w-1/2"
                      )}
                    >
                      <span className="text-[10px] font-semibold text-teal-500">
                        {getTypeSummary()}ID: {gridData.value}
                      </span>
                      <span className="text-sm font-semibold text-teal-500">
                        {(gridData.score ?? 0).toLocaleString()}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
      <div
        className={twMerge(
          isExpanded ? "columns-1 2xl:columns-4 mt-4" : "mt-3"
        )}
      >
        {originalDatasets.length > 0 &&
          originalDatasets.map((dataset: DatasetData, index: number) => (
            <DatasetSection
              key={dataset.dataset_id}
              isExpanded={isExpanded}
              dataset={dataset}
              ref={(el) => {
                if (dynamicChartRef.current)
                  dynamicChartRef.current[index] = el;
              }}
            />
          ))}
      </div>
    </>
  );
};

export default FilterByAnalysisArea;
